// shopUtils.js
'use strict';

import { ShopSectionType } from '../resource/shopConstants.js';

/**
 * Get product skus select path
 * @param {object} payload - payload.
 * @param {array} payload.prefixes - prefixes.
 * @param {array} payload.suffixes - suffixes.
 * @param {string} payload.shopId - shop id.
 * @param {string} payload.currency - currency.
 * @param {array} payload.types - types.
 * @param {array} payload.productIds - product ids.
 * @param {array} payload.categories - categories.
 * @param {array} payload.sectionIds - section ids.
 * @returns {array} select path
 */
export const getProductSkusSelectPath = ({
  prefixes = [],
  suffixes = [],
  shopId = '',
  currency = '',
  types = [],
  productIds = [],
  categories = [],
  sectionIds = [],
} = {}) => {
  const queries = [
    ['shopId', shopId],
    ['currency', currency],
    ['types', types],
    ['productIds', productIds],
    ['categories', categories],
    ['sectionIds', sectionIds],
  ];
  const queriesToString = queries
    .map(
      ([key, value]) =>
        `${key}=${Array.isArray(value) ? value.join(',') : value}`
    )
    .join('&');
  return [...prefixes, 'shop', 'productSkus', queriesToString, ...suffixes];
};

/**
 * Get payment source joint id
 * @param {object} payload - payload
 * @param {string} payload.id - payment source id
 * @param {string} payload.type - payment source type
 * @param {string} payload.via - payment gateway
 * @returns {string} payment source joint id
 */
export const getPaymentSourceJointId = ({ id, type, via } = {}) =>
  `${id}:${type}:${via}`;

/**
 * Parse payment source joint id
 * @param {object} payload - payload
 * @param {string} payload.jointId - payment source joint id
 * @returns {Source} payment source
 * @property {string} id - payment source id
 * @property {string} type - payment source type
 * @property {string} via - payment gateway
 */
export const parsePaymentSourceJointId = ({ jointId = '' } = {}) => {
  const items = jointId.split(':');
  const via = items.pop();
  const type = items.pop();
  const id = items.join(':');
  return { id, type, via };
};

/**
 * Get grouped sections by anchor
 * @param {array} sections - sections.
 * @returns {array} grouped sections
 * @example
 * getGroupedSectionsByAnchor({
 *   sections: [
 *     'ad=1',
 *     'anchor=1',
 *     'section=1',
 *     'section=2',
 *     'ad=2',
 *     'anchor=2',
 *     'section=3',
 *     'section=4',
 *   ],
 * });
 * @returns
 * [
 *   {
 *     id: 'ad=1',
 *     items: [],
 *   },
 *   {
 *     id: 'anchor=1',
 *     items: ['section=1', 'section=2', 'ad=2'],
 *   },
 *   {
 *     id: 'anchor=2',
 *     items: ['section=3', 'section=4'],
 *   },
 * ]
 */
export const getGroupedSectionsByAnchor = ({ sections = [] } = {}) => {
  const groups = [];

  let currentAnchorGroup = null;

  sections.forEach(item => {
    const [type] = item.split('=');
    if (ShopSectionType.ANCHOR === type) {
      if (currentAnchorGroup) {
        groups.push(currentAnchorGroup);
      }
      currentAnchorGroup = {
        id: item,
        items: [],
      };
    } else if (!currentAnchorGroup) {
      groups.push({
        id: item,
        items: [],
      });
    } else {
      currentAnchorGroup.items.push(item);
    }
  });

  if (currentAnchorGroup) {
    groups.push(currentAnchorGroup);
  }

  return groups;
};

export const getIsApplePay = ({ source }) => 'apple-pay' === source.type;

export const getIsTappayApplePay = ({ source }) =>
  getIsApplePay({ source }) && source.via.includes('tappay');

export const getIsEcpayApplePay = ({ source }) =>
  getIsApplePay({ source }) && source.via.includes('ecpay');

export const getIsNineoneappApplePay = ({ source }) =>
  getIsApplePay({ source }) && source.via.includes('nineoneapp');

/**
 * Check whether the user has filled in mobile carrier data.
 * @param {object} payload
 * @param {string} payload.carrierNumber - mobile carrier number.
 * @returns {boolean}
 * @example
 * getHasMobileCarrierData({ carrierNumber: '/11LX11S' });
 * // => true
 */
export const getHasMobileCarrierData = ({ carrierNumber } = {}) =>
  !!carrierNumber;

/**
 * Check whether the user has filled in member carrier data.
 * Requires email plus name / phone / address all present.
 * @param {object} payload
 * @param {string} payload.email - billing email.
 * @param {string} payload.name - recipient name.
 * @param {string} payload.phoneNumber - recipient phone.
 * @param {string} payload.address - recipient address.
 * @returns {boolean}
 * @example
 * getHasMemberCarrierData({
 *   email: 'a@b.com',
 *   name: 'X',
 *   phoneNumber: '0900',
 *   address: 'addr',
 * });
 * // => true
 */
export const getHasMemberCarrierData = ({
  email,
  name,
  phoneNumber,
  address,
} = {}) => !!email && !!name && !!phoneNumber && !!address;
